import type { BaseSlotName } from "../../config/platforms/types";
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import { PrebidPlugin } from "../PrebidPlugin/PrebidPlugin";
import { AdvertisementLabel } from "./ui/AdvertisementLabel";
import { InfoIcon } from "./ui/InfoIcon";
import invariant from "tiny-invariant";

export class DigitalServicesActPlugin extends AdEnginePlugin {
  private readonly adUnitsWithoutDynamicLabel: Partial<BaseSlotName>[] = [
    "interstitial",
    "floor_adhesion",
  ];
  private readonly defaultDSAPrebidSettings = {
    dsarequired: 1,
    pubrender: 2,
    datatopub: 2,
  };

  private extendPrebidConfig(pbjs: Prebid) {
    pbjs.mergeConfig({
      ortb2: {
        regs: {
          ext: {
            dsa: { ...this.defaultDSAPrebidSettings },
          },
        },
      },
    });
  }

  private getBidMeta(bid: PrebidBidResponse) {
    return bid.meta ?? {};
  }

  private getDSAMeta(bid: PrebidBidResponse): PrebidDsaObject | null {
    return this.getBidMeta(bid).dsa ?? null;
  }

  private isDSADataAvailable(bid: PrebidBidResponse): boolean {
    return (
      typeof this.getDSAMeta(bid)?.behalf === "string" &&
      typeof this.getDSAMeta(bid)?.adrender === "number"
    );
  }

  private handleRegularAdUnit(bid: PrebidBidResponse) {
    const { behalf, paid } = this.getDSAMeta(bid)!;
    const label = new AdvertisementLabel({
      slot: bid.adUnitCode as BaseSlotName,
      behalf,
      paid,
    }).render();
    document.getElementById(bid.adUnitCode)?.appendChild(label);
  }

  private handleTopLeaderboard(bid: PrebidBidResponse) {
    const topLeaderboardLabel = document.querySelector(
      ".top-ads-container .ae-translatable-label",
    )!;

    const { behalf, paid } = this.getDSAMeta(bid)!;

    const infoIcon = new InfoIcon({ behalf, paid }).render();

    topLeaderboardLabel.classList.add(
      "advertisement-label",
      "dsa-advertisement-label-clickable",
    );
    topLeaderboardLabel
      .querySelector(".dsa-advertisement-label-icon")
      ?.remove();
    topLeaderboardLabel.appendChild(infoIcon);
  }

  private handleIncontentSlots(bid: PrebidBidResponse, doc: Document | null) {
    const realSlotName = this.getRealSlotName(doc);

    if (!realSlotName) {
      return;
    }

    const incontentSlot = document.getElementById(realSlotName);
    if (!incontentSlot) return;

    const { behalf, paid } = this.getDSAMeta(bid)!;

    const label = new AdvertisementLabel({
      slot: bid.adUnitCode as BaseSlotName,
      behalf,
      paid,
    }).render();

    incontentSlot.appendChild(label);
  }

  private attachPrebidEventsHandlers(pbjs: Prebid) {
    const adRenderSucceededHandler: OnAdRenderSucceededHandler = ({
      bid,
      doc,
    }) => {
      if (
        !this.isDSADataAvailable(bid) ||
        this.adUnitsWithoutDynamicLabel.includes(bid.adUnitCode as BaseSlotName)
      )
        return;

      switch (bid.adUnitCode) {
        case "top_leaderboard":
          this.handleTopLeaderboard(bid);
          break;
        case "incontent_leaderboard":
        case "incontent_boxad":
          this.handleIncontentSlots(bid, doc);
          break;
        default:
          this.handleRegularAdUnit(bid);
      }
    };
    pbjs.onEvent("adRenderSucceeded", adRenderSucceededHandler);
  }

  private getRealSlotName(doc: Document | null): string | undefined {
    return doc?.defaultView?.frameElement?.parentElement?.parentElement?.id;
  }

  hooks: Hooks = {
    onBiddersInit: () => {
      const prebidPlugin = this.usePlugin(PrebidPlugin);
      invariant(prebidPlugin, "PrebidPlugin not found");

      prebidPlugin.addPrebidReadyCallback((pbjs: Prebid) => {
        this.extendPrebidConfig(pbjs);
        this.attachPrebidEventsHandlers(pbjs);
      });
    },
  };
}
